import type { SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, OnChanges } from '@angular/core';

import { FormBuilder, FormGroup } from '@angular/forms';

export interface FilterChip { key: string; text: string; }

export type DateRange = [Date, Date] | null;

export interface CatalogFilters {
  name: string | null;
  providers: string[];
  rates: string[];
  statuses: string[];
  catalogTypes: number[];
  creationRange: { from: string | null; to: string | null };
  receptionRange: { from: string | null; to: string | null };
  effectiveRange: { from: string | null; to: string | null };
  publicationRange: { from: string | null; to: string | null };
}

export type FieldKey =
  | 'name'
  | 'providers'
  | 'rates'
  | 'statuses'
  | 'typeCatalogs'
  | 'creationRange'
  | 'receptionRange'
  | 'effectiveRange'
  | 'publicationRange';

export interface SectionsVisibility {
  basic: boolean;
  dates: boolean;
}

export type FieldsVisibility = Partial<Record<FieldKey, boolean>>;

@Component({ standalone: false, selector: 'app-catalog-filter-drawer',
  templateUrl: './catalog-filter-drawer.component.html',
  styleUrls: ['./catalog-filter-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CatalogFilterDrawerComponent implements OnInit, OnChanges {
  private lastAppliedFilters: CatalogFilters | null = null;
  @Input() visible = false;

  @Input() appliedChips: FilterChip[] = [];

  /** Opciones para selects */
  @Input() providerOptions: string[] = [];
  @Input() rateOptions: string[] = [];
  @Input() statusOptions: string[] = [];
  @Input() catalogTypesOptions: number[] = [];

  @Input() stateLabel!: (s: any) => string;
  @Input() stateCatalogTypesLabel!: (s: any) => string;

  /** Título del drawer (puede ser string o TemplateRef si lo necesitas en el futuro) */
  @Input() drawerTitle = 'Filtros avanzados';

  /** Mostrar/ocultar secciones completas */
  @Input() sections: SectionsVisibility = { basic: true, dates: true };

  /** Mostrar/ocultar filtros individuales */
  @Input() fields: FieldsVisibility = {
    name: true,
    providers: true,
    rates: true,
    statuses: true,
    typeCatalogs: true,
    creationRange: true,
    receptionRange: true,
    effectiveRange: true,
    publicationRange: true
  };

  @Input() chipsVisible = true

  /** Hace colapsable la sección de fechas */
  @Input() collapsible = true;

  /** Estado inicial del colapso de fechas */
  @Input() datesCollapsed = false;

  /** Emite cuando el usuario cierra el drawer */
  @Output() close = new EventEmitter<void>();

  /** Emite los filtros aplicados ya normalizados */
  @Output() apply = new EventEmitter<CatalogFilters>();

  filtersForm!: FormGroup;

  constructor(private readonly fb: FormBuilder) {}

  ngOnInit(): void {
    this.filtersForm = this.fb.group({
      name: [null],
      providers: [[] as string[]],
      rates: [[] as string[]],
      statuses: [[] as string[]],
      catalogTypes: [[] as number[]],
      creationRange: [null as DateRange],
      receptionRange: [null as DateRange],
      effectiveRange: [null as DateRange],
      publicationRange: [null as DateRange]
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.filtersForm) return;

    if (changes['visible'] && changes['visible'].currentValue === true) {
      // Se acaba de abrir
      if (this.lastAppliedFilters) {
        this.loadFromFilters(this.lastAppliedFilters);
      }
    }
  }

  clearAdvancedFilters(): void {
    this.filtersForm.reset({
      name: null,
      providers: [],
      rates: [],
      statuses: [],
      creationRange: null,
      receptionRange: null,
      effectiveRange: null,
      publicationRange: null
    });

    this.applyAdvancedFilters();
  }

  applyAdvancedFilters(): void {
    const raw = this.filtersForm.getRawValue();
    const payload: CatalogFilters = {
      name: (raw.name ?? '')?.trim() || null,
      providers: raw.providers ?? [],
      rates: raw.rates ?? [],
      statuses: raw.statuses ?? [],
      catalogTypes: raw.catalogTypes ?? [],
      creationRange: this.normalizeRange(raw.creationRange),
      receptionRange: this.normalizeRange(raw.receptionRange),
      effectiveRange: this.normalizeRange(raw.effectiveRange),
      publicationRange: this.normalizeRange(raw.publicationRange)
    };
    this.buildChips(payload);
    this.lastAppliedFilters = payload;
    this.apply.emit(payload);
  }
  private readonly loadFromFilters = (f: CatalogFilters): void => {
    this.filtersForm.patchValue(
      {
        name: f.name,
        providers: f.providers ?? [],
        rates: f.rates ?? [],
        statuses: f.statuses ?? [],
        catalogTypes: f.catalogTypes ?? [],
      },
      { emitEvent: false }
    );

    this.buildChips(this.filtersForm.getRawValue());
  }

  removeChip(chip: FilterChip): void {
    switch (chip.key) {
      case 'name':             this.filtersForm.patchValue({ name: '' }); break;
      case 'providers':        this.filtersForm.patchValue({ providers: [] }); break;
      case 'rates':            this.filtersForm.patchValue({ rates: [] }); break;
      case 'statuses':         this.filtersForm.patchValue({ statuses: [] }); break;
      case 'creationRange':    this.filtersForm.patchValue({ creationRange: [] }); break;
      case 'receptionRange':   this.filtersForm.patchValue({ receptionRange: [] }); break;
      case 'effectiveRange':   this.filtersForm.patchValue({ effectiveRange: [] }); break;
      case 'publicationRange': this.filtersForm.patchValue({ publicationRange: [] }); break;
    }
    this.applyAdvancedFilters();
  }



  /** Convierte [Date,Date] a { from: 'yyyy-MM-dd', to: 'yyyy-MM-dd' } */
  private normalizeRange(range: DateRange): { from: string | null; to: string | null } {
    if (!range || range.length !== 2) return { from: null, to: null };
    const [from, to] = range;
    return {
      from: this.toISODate(from),
      to: this.toISODate(to)
    };
    // Si tu backend necesita fin de día en "to", puedes ajustarlo aquí.
  }

  private toISODate(d?: Date | null): string | null {
    if (!d) return null;
    // Asegura formato yyyy-MM-dd sin zona horaria
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // helper para template
  isVisible(key: FieldKey): boolean {
    return this.fields[key] !== false; // por defecto visible
  }

  private buildChips(v: any): void {
    const chips: FilterChip[] = [];
    if (v.name?.trim())        chips.push({ key: 'name', text: `Nombre: "${v.name.trim()}"` });
    if (v.providers?.length)   chips.push({ key: 'providers', text: `Proveedor: ${v.providers.join(', ')}` });
    if (v.rates?.length)       chips.push({ key: 'rates', text: `Tarifa: ${v.rates.join(', ')}` });
    if (v.statuses?.length)    chips.push({ key: 'statuses', text: `Estado: ${v.statuses.length} sel.` });
    if (v.catalogTypes?.length)    chips.push({ key: 'catalogTypes', text: `Tipos de catálogos: ${v.catalogTypes.length} sel.` });
    const fmt = (r: Date[]) => (r?.[0] ? r[0].toISOString().slice(0,10) : '…') + ' — ' + (r?.[1] ? r[1].toISOString().slice(0,10) : '…');
    if (v.creationRange?.length)    chips.push({ key: 'creationRange',    text: `Creación: ${fmt(v.creationRange)}` });
    if (v.receptionRange?.length)   chips.push({ key: 'receptionRange',   text: `Recepción: ${fmt(v.receptionRange)}` });
    if (v.effectiveRange?.length)   chips.push({ key: 'effectiveRange',   text: `Entrada en Vigor: ${fmt(v.effectiveRange)}` });
    if (v.publicationRange?.length) chips.push({ key: 'publicationRange', text: `Publicación: ${fmt(v.publicationRange)}` });

    this.appliedChips = chips;
  }

}
